import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Stack,
  Textarea,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useApp } from "../../context/AppContext";
import {
  CashDrawerSession,
  CashTransaction,
  ManualCashTransactionType,
  PayoutCategory,
} from "../../types/cashDrawer";

interface CashAdjustmentModalProps {
  opened: boolean;
  onClose: () => void;
  onRecorded: (transaction: CashTransaction) => void;
  session: CashDrawerSession;
  /**
   * Direction is set by whichever button (Pay In / Pay Out) the staff clicked
   * in the Cash Drawer modal — it's not user-editable from inside this modal.
   */
  direction: "in" | "out";
}

type Direction = "in" | "out";

const directionToType = (direction: Direction): ManualCashTransactionType =>
  direction === "in" ? "cash_in" : "cash_out";

export const CashAdjustmentModal: React.FC<CashAdjustmentModalProps> = ({
  opened,
  onClose,
  onRecorded,
  session,
  direction,
}) => {
  const { recordCashAdjustment } = useApp();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [category, setCategory] = useState<PayoutCategory>("expense");
  const [amount, setAmount] = useState<number | string>("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setCategory("expense");
      setAmount("");
      setDescription("");
      setError(null);
    }
  }, [opened, direction]);

  const handleSubmit = async () => {
    const amountNum = Number(amount) || 0;
    if (amountNum <= 0) {
      setError("Amount must be greater than zero");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const tx_type = directionToType(direction);
      const result = await recordCashAdjustment(session.id, {
        type: tx_type,
        amount: amountNum,
        description: description || undefined,
        ...(tx_type === "cash_out" ? { payout_category: category } : {}),
      });
      onRecorded(result.transaction);
      onClose();
    } catch (e: any) {
      setError(e?.message || "Failed to record transaction");
    } finally {
      setSubmitting(false);
    }
  };

  const title = direction === "in" ? "Record Pay In" : "Record Pay Out";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      fullScreen={isMobile}
    >
      <Stack gap="md">
        {direction === "out" && (
          <SegmentedControl
            fullWidth
            value={category}
            onChange={(v) => setCategory(v as PayoutCategory)}
            data={[
              { label: "Expense", value: "expense" },
              { label: "Deposit", value: "deposit" },
            ]}
            color="var(--brand-teal)"
          />
        )}

        <NumberInput
          label="Amount"
          prefix="$"
          min={0}
          decimalScale={2}
          fixedDecimalScale
          value={amount}
          onChange={(v) => setAmount(v)}
        />

        <Textarea
          label="Description"
          description={
            direction === "in"
              ? "Why is cash being added to the drawer?"
              : "Where is this cash going?"
          }
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          minRows={2}
        />

        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleSubmit}
            loading={submitting}
          >
            Record {direction === "in" ? "Pay In" : "Pay Out"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CashAdjustmentModal;
